import { useState, useEffect, useCallback } from "react";

export const ZOOM_OPTIONS = ["80%", "100%", "120%", "140%"] as const;
export type ZoomOption = (typeof ZOOM_OPTIONS)[number];

const ZOOM_STORAGE_KEY = "zoomDefault";
const RESPONSIVE_BREAKPOINT = 1425;
const PORTAL_STYLE_ID = "zoom-portal-override";

function getInitialZoom(): ZoomOption {
  const saved = localStorage.getItem(ZOOM_STORAGE_KEY) as ZoomOption | null;
  if (saved && ZOOM_OPTIONS.includes(saved)) return saved;
  return window.innerWidth < RESPONSIVE_BREAKPOINT ? "80%" : "100%";
}

/**
 * Componentes MUI com portal (Dialog, Menu, Popover, Tooltip, Autocomplete, Select, Modal)
 * renderizam direto no <body>, fora do #root.
 * Como o zoom esta no #root, esses portais ficam sem zoom.
 * Essa funcao injeta CSS que aplica o mesmo zoom ao CONTEUDO desses portais,
 * sem afetar o posicionamento dos containers (backdrop, popper root, etc).
 *
 * Issues relevantes:
 * - https://github.com/mui/material-ui/issues/32777
 * - https://github.com/mui/material-ui/issues/40408
 * - https://github.com/floating-ui/floating-ui/issues/3032
 */
function setPortalZoom(scale: number) {
  let styleEl = document.getElementById(PORTAL_STYLE_ID) as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement("style");
    styleEl.id = PORTAL_STYLE_ID;
    document.head.appendChild(styleEl);
  }
  if (scale === 1) {
    styleEl.textContent = "";
    return;
  }
  styleEl.textContent = `
    body > .MuiDialog-root .MuiDialog-paper {
      zoom: ${scale} !important;
    }
    body > .MuiPopover-root .MuiPopover-paper {
      scale: ${scale} !important;
      transform-origin: 0 0 !important;
    }
    body > .MuiPopper-root .MuiPaper-root {
      scale: ${scale} !important;
      transform-origin: 65% 0 !important;
    }
    body > .MuiTooltip-popper .MuiTooltip-tooltip {
      zoom: ${scale} !important;
    }
    body > .MuiModal-root:not(.MuiPopover-root):not(.MuiDialog-root) > *:not(.MuiBackdrop-root) {
      zoom: ${scale} !important;
    }
  `;
}

export function useZoom() {
  const [zoom, setZoom] = useState<ZoomOption>(getInitialZoom);

  useEffect(() => {
    localStorage.setItem(ZOOM_STORAGE_KEY, zoom);
    const scale = parseFloat(zoom) / 100;
    const compensatedSize = `${(1 / scale) * 100}`;

    const root = document.getElementById("root");
    if (root) {
      (root.style as CSSStyleDeclaration & { zoom: string }).zoom = `${scale}`;
      root.style.height = `${compensatedSize}vh`;
      const rootChild = root.children[1] as HTMLElement | undefined;
      if (rootChild) {
        rootChild.style.height = `100%`;
      }
    }

    setPortalZoom(scale);
  }, [zoom]);

  useEffect(() => {
    return () => {
      const root = document.getElementById("root");
      if (root) {
        (root.style as CSSStyleDeclaration & { zoom: string }).zoom = "";
        root.style.height = "";
        root.style.width = "";
      }
      document.getElementById(PORTAL_STYLE_ID)?.remove();
    };
  }, []);

  const zoomIndex = ZOOM_OPTIONS.indexOf(zoom);

  const canIncrease = zoomIndex < ZOOM_OPTIONS.length - 1;
  const canDecrease = zoomIndex > 0;

  const increase = useCallback(() => {
    setZoom((prev) => {
      const idx = ZOOM_OPTIONS.indexOf(prev);
      return idx < ZOOM_OPTIONS.length - 1 ? ZOOM_OPTIONS[idx + 1] : prev;
    });
  }, []);

  const decrease = useCallback(() => {
    setZoom((prev) => {
      const idx = ZOOM_OPTIONS.indexOf(prev);
      return idx > 0 ? ZOOM_OPTIONS[idx - 1] : prev;
    });
  }, []);

  return { zoom, canIncrease, canDecrease, increase, decrease };
}
